import { Suspense, lazy, useEffect, useState } from 'react';

import { copy } from '../core/copy';
import { unseenEcho, unseenInbox } from '../core/posada';
import { countRevisions, countWorks, currentIsland, unlockedFeatures } from '../core/rules';
import { NAV_ORDER, Nav, Toasts } from './components';
import { soundEnabled, toggleSound } from './audio';
import {
  ackPosada,
  boot,
  contentOf,
  go,
  logout,
  openDuel,
  markFeatureSeen,
  playerOf,
  posadaOf,
  toast,
  useStore,
  watchConnection,
  type Screen,
} from './store';
/**
 * The world builder is a big screen that two or three people ever open. Splitting it out keeps it
 * out of the bundle every student downloads on a phone; it arrives when the gear icon is clicked.
 */
const Admin = lazy(() => import('./screens/Admin').then((m) => ({ default: m.Admin })));
import { Dnevnik } from './screens/Dnevnik';
import { Dom } from './screens/Dom';
import { Confirm, Login } from './screens/Login';
import { Mapa } from './screens/Mapa';
import { NodeCard } from './screens/NodeCard';
import { Onboarding } from './screens/Onboarding';
import { Ostrvo } from './screens/Ostrvo';
import { PathSwitch } from './screens/PathSwitch';
import { Dvoboj } from './screens/Dvoboj';
import { Posada } from './screens/Posada';
import { Sada } from './screens/Sada';

export function App() {
  const phase = useStore((s) => s.phase);
  const [route, setRoute] = useState(() => window.location.pathname);

  useEffect(() => {
    void boot();
    return watchConnection();
  }, []);

  /**
   * If the very first request never lands, retry rather than leaving somebody looking at a logo.
   * Cheap insurance: this fires only while the app is still on the loading screen.
   */
  useEffect(() => {
    if (phase !== 'loading') return;
    const timer = window.setTimeout(() => void boot(), 6000);
    return () => window.clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /*
   * A duel, opened from the link in the Skool post. Deferred until the app is up: this only marks
   * where to go, and the normal login flow decides whether anybody gets there.
   */
  useEffect(() => {
    if (!route.startsWith('/dvoboj/')) return;
    if (phase !== 'game') return;
    const id = decodeURIComponent(route.slice('/dvoboj/'.length));
    if (id) void openDuel(id);
  }, [route, phase]);

  if (route.startsWith('/ulaz')) {
    return (
      <Shell bare>
        <Confirm />
      </Shell>
    );
  }

  if (phase === 'loading') {
    return (
      <Shell bare>
        <div className="centered">
          <img src="/brand/talas-white.svg" alt="Talas" className="logo-mark" style={{ width: 160, opacity: 0.7 }} />
        </div>
      </Shell>
    );
  }

  if (phase === 'login') {
    return (
      <Shell bare>
        <Login />
      </Shell>
    );
  }

  if (phase === 'onboarding') {
    return (
      <Shell bare>
        <Onboarding />
      </Shell>
    );
  }

  return <Game route={route} onRoute={setRoute} />;
}

/* ------------------------------------------------------------------ shell */

function Shell({ children, bare }: { children: React.ReactNode; bare?: boolean }) {
  return (
    <div className="app">
      <div className="frame">
        {bare ? <div className="stage">{children}</div> : children}
        <Toasts />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ the crew, announced */

/**
 * The two things another person did, said once each.
 *
 * A comment arriving is a courtesy — the revision card carries the detail, so this only stops it
 * being a surprise. The other one is the whole reward of the feature: somebody you helped went
 * and did the step that most people skip, and that is the only sentence in this product that
 * tells one member their effort landed on another.
 *
 * Announced ids are remembered for the life of the tab, so a failed acknowledgement means a
 * missing toast rather than the same toast every time state changes.
 */
function useAnnouncePosada(): void {
  const posada = useStore(posadaOf);

  useEffect(() => {
    const fresh = [...unseenInbox(posada), ...unseenEcho(posada)].filter(
      (item) => !announced.has(item.id),
    );
    if (fresh.length === 0) return;

    for (const item of fresh) {
      announced.add(item.id);
      if ('revisedAt' in item) {
        if (item.revisedAt) toast(copy.posada.echoRevised, copy.posada.echoRevisedBody(item.toName), 'gold');
        else if (item.thankedAt) toast(copy.posada.echoThanks(item.toName), undefined, 'green');
      } else {
        toast(copy.posada.gotTitle, copy.posada.from(item.fromName));
      }
    }
    void ackPosada(fresh.map((item) => item.id));
  }, [posada]);
}

const announced = new Set<string>();

/* ------------------------------------------------------------------ the game shell */

function Game({ route, onRoute }: { route: string; onRoute: (path: string) => void }) {
  const server = useStore((s) => s.server);
  const screen = useStore((s) => s.screen);
  const openIsland = useStore((s) => s.openIsland);
  const openNodeId = useStore((s) => s.openNode);
  const online = useStore((s) => s.online);
  const pending = useStore((s) => s.pending);
  const contentStale = useStore((s) => s.contentStale);
  const seen = useStore((s) => s.seenFeatures);
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const [sound, setSound] = useState(soundEnabled);
  const [settings, setSettings] = useState(false);

  const features = unlockedFeatures(content, player);
  const fresh = features.filter((feature) => !seen.includes(feature));

  // Announce a screen the first time it appears, then never again.
  useEffect(() => {
    for (const feature of features) {
      if (seen.includes(feature)) continue;
      if (feature === 'sada' || feature === 'mapa') {
        markFeatureSeen(feature);
        continue;
      }
      // Features whose screens ship in a later phase (LIK, POSADA) unlock silently for now.
      // Not marked seen either, so the announcement still fires the day the screen exists.
      if (!NAV_ORDER.includes(feature as Screen)) continue;
      const name = copy.nav[feature as keyof typeof copy.nav];
      if (name) toast(copy.toast.newScreen(name), undefined, 'gold');
      markFeatureSeen(feature);
    }
    // `seen` is intentionally omitted: marking a feature seen must not retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features.join(',')]);

  useAnnouncePosada();

  if (!server) return null;

  const isAdminRoute = route.startsWith('/admin');
  const island = currentIsland(content, player);
  const works = countWorks(content, player);
  const revisions = countRevisions(content, player);

  const navigate = (screen: Screen) => {
    if (isAdminRoute) {
      history.pushState(null, '', '/');
      onRoute('/');
    }
    go(screen);
  };

  return (
    <div className="app">
      <div className="frame">
        <a className="skip-link" href="#main">
          Preskoči na sadržaj
        </a>

        <header className="topbar">
          <img src="/brand/talas-white.svg" alt="Talas" className="wordmark" />
          <span className="where">
            {copy.stats.islandLabel} {island.name}
            {/* On a phone the island name is all that fits, and it is the part that matters. */}
            <span className="wide-only">
              {' · '}
              {copy.stats.dayLabel} {player.day}
              {works > 0 ? ` · ${copy.stats.line(works, revisions)}` : ''}
            </span>
          </span>
          <span className="spacer" />
          {server.member.role === 'admin' ? (
            <button
              className="iconbtn"
              title={copy.admin.title}
              onClick={() => {
                history.pushState(null, '', isAdminRoute ? '/' : '/admin');
                onRoute(isAdminRoute ? '/' : '/admin');
              }}
            >
              ⚙
            </button>
          ) : null}
          <button
            className="iconbtn"
            title={copy.settings.title}
            onClick={() => setSettings(true)}
          >
            ◆
          </button>
          <button
            className="iconbtn"
            title={copy.a11y.sound}
            aria-pressed={sound}
            onClick={() => setSound(toggleSound())}
          >
            {sound ? '♪' : '×'}
          </button>
          <button className="iconbtn" title="Izlaz" onClick={() => void logout()}>
            ⏻
          </button>
        </header>

        {!online ? (
          <div className="offline-bar">
            {copy.error.offline}
            {pending > 0 ? ` · ${pending} čeka slanje` : ''}
          </div>
        ) : null}

        {/*
          A publish normally reaches an open tab as a silent content fetch (store.ts). This banner
          is the fallback for the one case a fetch cannot fix: the running bundle is too old to
          parse what the server now serves, so the rules engine here would be reasoning about a
          curriculum it cannot read. Say so rather than quietly disagreeing.
        */}
        {contentStale ? (
          <button className="offline-bar" onClick={() => window.location.reload()}>
            {copy.error.stale} · {copy.error.reload}
          </button>
        ) : null}

        {server.member.status === 'revoked' ? (
          <div className="offline-bar">{copy.login.readOnly}</div>
        ) : null}

        {/* The arena fills the frame the way the map does — a battle screen with page padding
            around it is a screenshot of a game rather than a game. */}
        <main
          id="main"
          className={`stage ${
            (screen === 'mapa' || screen === 'dvoboj') && !isAdminRoute ? 'flush' : ''
          }`.trim()}
        >
          {isAdminRoute ? (
            <Suspense fallback={<p className="pad muted">…</p>}>
              <Admin />
            </Suspense>
          ) : screen === 'sada' ? (
            <Sada />
          ) : screen === 'mapa' ? (
            <Mapa />
          ) : screen === 'ostrvo' ? (
            <Ostrvo islandId={openIsland} />
          ) : screen === 'dom' ? (
            <Dom />
          ) : screen === 'posada' ? (
            <Posada />
          ) : screen === 'dvoboj' ? (
            <Dvoboj />
          ) : (
            <Dnevnik />
          )}
        </main>

        {!isAdminRoute ? (
          <Nav
            features={features}
            current={screen === 'ostrvo' ? 'mapa' : screen}
            onGo={navigate}
            fresh={fresh}
          />
        ) : null}

        {settings ? <PathSwitch onClose={() => setSettings(false)} /> : null}
        {openNodeId ? <NodeCard nodeId={openNodeId} /> : null}
        <Toasts />
      </div>
    </div>
  );
}
